<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <h6 class="mb-0">{{ $t('projects.project_show_feeds')}}
        <span v-if="planLevel === 'basic'">({{ this.activeFeeds.length }} / 2)</span>
        <span v-if="planLevel === 'pro'">({{ this.activeFeeds.length }} / 4)</span>
      </h6>
      <div class="alert alert-danger d-flex align-items-center" v-if="limitExceeded">
        <img class="icon-small mr-4" v-bind:src="'/app/images/icons/warning_danger.svg'" />
        <span class="mr-2">Limit dostępnych feedów został osiągnięty.</span>
        <router-link :to="{ name: 'settings'}">Zmień plan.</router-link>
      </div>
      <div class="d-flex align-items-center">
        <router-link :to="{ name: 'feed.create.main-settings', params: { projectId: this.projectId, feed: feed }}" class="mr-2" v-bind:class="{ 'disabled-router-link': noProducts || limitExceeded }">
          <button :disabled="noProducts || limitExceeded" type="button" class="btn btn-large btn-orange-dark">
            <plus-circle-icon></plus-circle-icon>
            <span>{{ $t('feeds.add_new_feed') }}</span>
          </button>
        </router-link>
      </div>
    </template>
    <template slot="dashboard-body">
      <div v-if="!isError">
        <div v-if="!isLoading">
          <div v-if="activeFeeds.length > 0">
            <p class="m-4"><strong>{{ $t('feeds.active') }}</strong></p>
            <div class="dashboard-content" v-for="feed in activeFeeds" v-bind:key="feed.id">
              <feed-summary
                @onFeedDelete="deleteFeed"
                @onFeedStatusUpdate="updateFeedStatus"
                :alerts="alerts"
                :feed="feed"
                :feedsDirectoryUrl="feedsDirectoryUrl"
                :feedTypes="feedTypes"
                :noProducts="noProducts">
              </feed-summary>
            </div>
          </div>
          <div v-if="inactiveFeeds.length > 0">
            <p class="m-4"><strong>{{ $t('feeds.inactive') }}</strong></p>
            <div class="dashboard-content" v-for="feed in inactiveFeeds" v-bind:key="feed.id">
              <feed-summary
                @onFeedDelete="deleteFeed"
                @onFeedStatusUpdate="updateFeedStatus"
                :alerts="alerts"
                :feed="feed"
                :feedsDirectoryUrl="feedsDirectoryUrl"
                :feedTypes="feedTypes"
                :noProducts="noProducts">
              </feed-summary>
            </div>
          </div>
          <div v-if="noFeeds" class="dashboard-content mt-4">
            <div class="alert alert-warning mb-4 mt-3" role="alert">
              <img width="40" height="40" src="/app/images/icons/warning.svg" class="mr-3" />
              {{ $t('feeds.no_feeds') }}
            </div>
          </div>
          <div v-if="noProducts" class="dashboard-content mt-4">
            <div v-if="noSources">
              <div class="alert alert-danger mt-4 mb-5 d-flex align-items-center" role="alert">
                <img width="40" height="40" src="/app/images/icons/warning_danger.svg" class="mr-4" />
                <p class="mb-0">{{ $t('feeds.feed_cannot_create_title2') }}</p>
              </div>
              <router-link :to="{ name: 'source.create', params: { projectId: this.projectId }}">
                <button type="button" class="btn btn-large btn-orange-dark">
                  <plus-circle-icon></plus-circle-icon>
                  <span>{{ $t('sources.add_new_file') }}</span>
                </button>
              </router-link>
            </div>
            <div v-else>
              <div class="alert alert-danger my-3 d-flex align-items-center" role="alert">
                <img width="40" height="40" src="/app/images/icons/warning_danger.svg" class="mr-4" />
                <div>
                  <p class="mb-0">{{ $t('feeds.feed_cannot_create_title1') }}</p>
                  <p class="mb-0">{{ $t('feeds.feed_cannot_create_desc1') }}</p>
                  <p class="mb-0">{{ $t('projects.project_update_called_previously') }}</p>
                  <p class="mb-0">{{ $t('projects.project_update_not_called_previously') }}</p>
                </div>
              </div>
              <div class="mt-5">
                <button type="button" class="btn btn-large btn-orange" @click="updateProjectFeeds(projectId)">
                  <refresh-cw-icon></refresh-cw-icon>
                  <span>{{ $t('projects.project_show_update_now') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="spinner-border text-orange" role="status"></div>
        </div>
      </div>
      <div v-else>
        <div class="dashboard-content mt-4">
          <div class="alert alert-danger my-3" role="alert">
            <p>{{ $t('global.error_heading') }}</p>
            <p><strong>{{ $t('global.error_code') }} {{ errorData.code}}</strong></p>
            <p><strong>{{ $t('global.error_type') }} {{ errorData.type}}</strong></p>
            <p><strong>{{ $t('global.error_status') }}: {{ errorData.message}}</strong></p>
            <p>{{ $t('global.contact_the_administrator') }}</p>
          </div>
        </div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import axios from 'axios'
import DashboardTemplate from '@/views/Dashboard'
import FeedSummary from './Partials/FeedSummary'
import { PlusCircleIcon, RefreshCwIcon } from 'vue-feather-icons'
import getAlerts from '@/mixins/getAlerts'
import getFeeds from '@/mixins/getFeeds'
import getFeedTypes from '@/mixins/getFeedTypes'
import getProducts from '@/mixins/getProducts'
import getSources from '@/mixins/getSources'
import updateFeeds from '@/mixins/updateFeeds'

export default {
  name: 'Feeds',

  data () {
    return {
      errorData: {},
      feed: { name: '' },
      feedsDirectoryUrl: process.env.VUE_APP_FEEDS_DIRECTORY,
      planLevel: this.$attrs.project.planLevel,
      projectId: null
    }
  },

  mixins: [getAlerts, getFeeds, getFeedTypes, getProducts, getSources, updateFeeds],

  created () {
    this.projectId = Number(this.$route.params.id)
    this.getFeedTypes()
    this.getProjectFeeds(this.projectId)
    this.getProjectAlerts(this.projectId)
    this.getProjectSources(this.projectId)
    this.getProjectProducts(this.projectId)
    if (localStorage.feed) this.feed = JSON.parse(localStorage.getItem('feed'))
  },

  methods: {
    updateFeedStatus (feed) {
      const feedConfig = {
        name: feed.name,
        active: !feed.active,
        config: feed.config,
        feedType: feed.feedType
      }

      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/feeds/update/' + this.projectId + '/' + feed.id, feedConfig)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            if (!feed.active) {
              this.$bvToast.toast(this.$t('feeds.feed_activation_success_description') + feed.id, {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('feeds.feed_activation_success_heading'),
                variant: 'success'
              })
            } else {
              this.$bvToast.toast(this.$t('feeds.feed_deactivation_success_description') + feed.id, {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('feeds.feed_deactivation_success_heading'),
                variant: 'success'
              })
            }
            feed.active = !feed.active
          }
        })
        .catch((error) => {
          if (error.response) {
            if (!feed.active) {
              this.$bvToast.toast(this.$t('feeds.feed_activation_error_description') + feed.id, {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('feeds.feed_activation_error_heading'),
                variant: 'danger'
              })
            } else {
              this.$bvToast.toast(this.$t('feeds.feed_deactivation_error_description') + feed.id, {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('feeds.feed_deactivation_error_heading'),
                variant: 'danger'
              })
            }
          }
        })
    },

    deleteFeed (feedId) {
      const feedToDeleteIndex = this.feeds.findIndex(feed => feed.id === feedId)
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/feeds/delete/' + this.projectId + '/' + feedId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$bvToast.toast(this.$t('feeds.feed_delete_success_description') + feedId, {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('feeds.feed_delete_success_heading'),
              variant: 'success'
            })
            this.feeds.splice(feedToDeleteIndex, 1)
            this.$attrs.project.feeds.splice(this.$attrs.project.feeds.findIndex(f => f.id === feedId), 1)
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$bvToast.toast(this.$t('feeds.feed_delete_error_description') + feedId, {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('feeds.feed_delete_error_heading'),
              variant: 'danger'
            })
          }
        })
    }
  },

  computed: {
    activeFeeds () {
      return this.feeds.filter(feed => feed.active === true)
    },

    inactiveFeeds () {
      return this.feeds.filter(feed => feed.active === false)
    },

    noFeeds () {
      if (this.feeds.length > 0) return false
      else return true
    },

    noSources () {
      if (this.sources.length > 0) return false
      else return true
    },

    noProducts () {
      if (this.products.length > 0) return false
      else return true
    },

    limitExceeded () {
      const feedsCount = this.feeds.filter(feed => feed.active === true).length
      if ((this.planLevel === 'basic') && (feedsCount > 1)) {
        return true
      } else if ((this.planLevel === 'pro') && (feedsCount > 3)) {
        return true
      } else return false
    },

    isLoading () {
      if (this.isLoadingAlerts || this.isLoadingFeeds || this.isLoadingFeedTypes || this.isLoadingProducts || this.isLoadingSources) return true
      else return false
    },

    isError () {
      if (this.isErrorAlerts || this.isErrorFeeds || this.isErrorFeedTypes || this.isErrorSources) return true
      else return false
    }
  },

  components: { DashboardTemplate, FeedSummary, PlusCircleIcon, RefreshCwIcon }
}
</script>
